// geschaeftlichFirma.ts — Unternehmen eines geschäftlichen Kommunikationswegs
// einer Person.
//
// Seit 02.10.2026 kann ein geschäftlicher Weg (Telefon, Mobil, Fax, E-Mail)
// MEHREREN Unternehmen gehören (User-Entscheidung):
//   - zur Auswahl stehen nur AKTIVE Firmen — ausgeschiedene nicht,
//   - „(Keine Firma)“ gibt es nicht mehr: Hat die Person genau EINE aktive
//     Firma, ist sie immer gesetzt; ab zwei darf die Auswahl leer bleiben
//     (= keine Zuordnung).
//
// Die „genau eine“-Regel ist ABGELEITET, nicht in den Formular-State
// geschrieben: Die Maske zeigt und der Builder speichert die „effektiven“
// Firmen. So macht das bloße Öffnen einer Akte nichts „geändert“ (kein
// Leave-Dialog), und trotzdem geht die Zuordnung mit, sobald gespeichert wird
// (anlageUpdatePayload.ts).
//
// Gespeichert wird an den drei festen Feldern in `commCompanies` (Feld →
// Liste), an weiteren Einträgen als `firmen`. Ältere Stände (eine Firma als
// Objekt, `null` = bewusst keine, `companyId`/`companyName`/`keineFirma` an
// weiteren Einträgen) werden weiter gelesen.

import type { FormState } from "./formUtils";

export type FirmaOption = { id?: number; name: string };

/** `commCompanies` der Maske: Feld → gewählte Firmen (Altstand: eine Firma oder `null`). */
export type CommCompanies = Record<string, FirmaOption[] | FirmaOption | null | undefined>;

const parseArr = (raw: unknown): any[] => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

const text = (v: unknown): string => (v == null ? "" : String(v)).trim();

const positiveId = (v: unknown): number | undefined => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

/** Nur `id` und `name` — ohne Zusatzfelder wie `aktiv`. */
export const firmaKern = (f: FirmaOption): FirmaOption => (f.id ? { id: f.id, name: f.name } : { name: f.name });

/** Dieselbe Firma? Über die Id, wenn beide eine haben, sonst über den Namen. */
export function gleicheFirma(a: FirmaOption, b: FirmaOption): boolean {
  if (a.id && b.id) return a.id === b.id;
  return text(a.name).toLowerCase() === text(b.name).toLowerCase();
}

/** Liste ohne Dubletten und ohne namenlose Einträge, Reihenfolge bleibt. */
export function firmenEindeutig(firmen: readonly FirmaOption[]): FirmaOption[] {
  const out: FirmaOption[] = [];
  for (const f of firmen) {
    if (!f || !text(f.name)) continue;
    if (!out.some((o) => gleicheFirma(o, f))) out.push(firmaKern({ id: positiveId(f.id), name: text(f.name) }));
  }
  return out;
}

/**
 * Die Firmen, denen ein geschäftlicher Weg zugeordnet werden kann: der
 * Arbeitgeber aus „Berufliche Angaben“ und die Firmen-Verknüpfungen
 * (`rel_personFirma`). `aktiv` = nicht ausgeschieden — nur aktive Firmen stehen
 * zur Auswahl und zählen für „genau eine“.
 */
export function geschaeftlicheFirmen(formFields: FormState): Array<FirmaOption & { aktiv: boolean }> {
  const map = new Map<string, FirmaOption & { aktiv: boolean }>();

  const empName = text(formFields.employerContactName);
  if (empName) {
    map.set(empName.toLowerCase(), {
      id: positiveId(text(formFields.employerContactId)),
      name: empName,
      aktiv: true,
    });
  }

  for (const it of parseArr(formFields.rel_personFirma)) {
    const firma = it?.verant?.isExtern ? it?.verant?.externalCompany : it?.ref;
    const name = text(firma?.displayName || firma?.lastName);
    if (!name) continue;
    const key = name.toLowerCase();
    const aktiv = it?.ausgeschieden !== true;
    const vorhanden = map.get(key);
    if (vorhanden) {
      // Arbeitgeber und Verknüpfung sind dieselbe Firma — aktiv, wenn eine der
      // beiden es ist.
      vorhanden.aktiv = vorhanden.aktiv || aktiv;
      continue;
    }
    map.set(key, { id: positiveId(firma?.contactId ?? firma?.id), name, aktiv });
  }

  return Array.from(map.values());
}

/** Die wählbaren Firmen: nur aktive (ausgeschiedene stehen nicht zur Auswahl). */
export function waehlbareFirmen(firmen: ReadonlyArray<FirmaOption & { aktiv?: boolean }>): FirmaOption[] {
  return firmen.filter((f) => f.aktiv !== false).map(firmaKern);
}

/** Genau EINE aktive Firma → diese, sonst `null` (bei 0 oder ≥ 2 nichts raten). */
export function einzigeFirma(firmen: ReadonlyArray<FirmaOption & { aktiv?: boolean }>): FirmaOption | null {
  const aktive = waehlbareFirmen(firmen);
  return aktive.length === 1 ? aktive[0] : null;
}

/**
 * Gespeicherter Wert eines Felds als Liste — `undefined` = nichts gespeichert.
 * Altstände: ein Objekt → `[objekt]`, `null` („(Keine Firma)“) → `[]`.
 */
export function gespeicherteFirmen(wert: FirmaOption[] | FirmaOption | null | undefined): FirmaOption[] | undefined {
  if (wert === undefined) return undefined;
  if (wert === null) return [];
  if (Array.isArray(wert)) return firmenEindeutig(wert);
  return text(wert.name) ? firmenEindeutig([wert]) : [];
}

/**
 * Wirksame Firmen: die gespeicherten — und hat die Person genau EINE aktive
 * Firma, ist sie immer dabei (vorn, wenn sie noch fehlte). Ohne gespeicherte
 * Auswahl und bei 0 oder ≥ 2 aktiven Firmen: keine.
 */
export function effektiveFirmen(
  gespeichert: FirmaOption[] | undefined,
  firmen: ReadonlyArray<FirmaOption & { aktiv?: boolean }>,
): FirmaOption[] {
  const basis = gespeichert ?? [];
  const einzige = einzigeFirma(firmen);
  if (einzige && !basis.some((f) => gleicheFirma(f, einzige))) return [einzige, ...basis];
  return basis;
}

/** Wirksame Firmen eines festen geschäftlichen Felds (`phoneWork`, `mobileWork`, `emailWork`). */
export function slotFirmen(
  commCompanies: CommCompanies,
  feld: string,
  firmen: ReadonlyArray<FirmaOption & { aktiv?: boolean }>,
): FirmaOption[] {
  const gespeichert = Object.prototype.hasOwnProperty.call(commCompanies, feld)
    ? gespeicherteFirmen(commCompanies[feld])
    : undefined;
  return effektiveFirmen(gespeichert, firmen);
}

/** Ein Eintrag unter „Weitere Kommunikation“, soweit hier relevant. */
export type WeitererWeg = {
  zuordnung?: string;
  kind?: string;
  firmen?: FirmaOption[];
  /** Altstand (eine Firma). */
  companyId?: unknown;
  companyName?: unknown;
  keineFirma?: boolean;
};

/** Gespeicherte Firmen eines weiteren Eintrags (Altstände eingeschlossen). */
export function weitereGespeichert(eintrag: WeitererWeg): FirmaOption[] | undefined {
  if (Array.isArray(eintrag.firmen)) return firmenEindeutig(eintrag.firmen);
  const name = text(eintrag.companyName);
  if (name) return [firmaKern({ id: positiveId(eintrag.companyId), name })];
  if (eintrag.keineFirma) return [];
  return undefined;
}

/** Wirksame Firmen eines weiteren Eintrags — nur geschäftliche bekommen welche. */
export function weitereFirmen(
  eintrag: WeitererWeg,
  firmen: ReadonlyArray<FirmaOption & { aktiv?: boolean }>,
): FirmaOption[] {
  const art = text(eintrag.zuordnung || eintrag.kind);
  if (art !== "geschaeftlich") return [];
  return effektiveFirmen(weitereGespeichert(eintrag), firmen);
}

/** Die Firmen-Ids einer Auswahl (Firmen ohne Id — noch nicht angelegt — fallen weg). */
export const firmenIds = (firmen: readonly FirmaOption[]): number[] =>
  firmenEindeutig(firmen)
    .map((f) => positiveId(f.id))
    .filter((id): id is number => id != null);

/** Die Namen einer Auswahl, für Anzeige und Label. */
export const firmenNamen = (firmen: readonly FirmaOption[]): string[] => firmenEindeutig(firmen).map((f) => f.name);
